<template>
  <component
    class="card"
    :class="{ [size]: true }"
    :is="componentType"
    :to="toIfLink"
  >
    <div class="image-container">
      <div class="image" v-lazy:background-image="backgroundImage" />
      <video-overlay v-if="isVideo" />
    </div>
    <div class="details">
      <div class="details-container">
        <div class="icon-wrapper" v-if="iconUrl">
          <icon class="icon" :icon="iconUrl"/>
        </div>
        <p class="date" v-if="fromDate || toDate">
          <date-formatter :timeStamp="fromDate" :timezone="timezone" :type="timeFormat" />
          <span v-if="toDate"> -
            <date-formatter :timeStamp="toDate" :timezone="timezone" :type="timeFormat" />
          </span>
        </p>
        <p class="name">{{ name }}</p>
        <p class="description" v-if="description">{{ description }}</p>

        <venue-name :name="houseLabel" />

        <event-status
          class="status"
          v-if="status"
          :status="status"
          :date="opensAt"
          :timezone="timezone"
          :guests="guests"
          :ticketless="ticketless"
          :isActive="isActive"
          :digital="digital"
        />
          <sh-button
            v-if="hasLabel"
            class="label"
            color="primary"
            :to="to"
            tag="nuxt-link"
          >
            {{ $t(label) }}
          </sh-button>
      </div>
    </div>
  </component>
</template>

<script>
import VideoOverlay from '~/components/video-overlay'
import { hasAllowedKeys } from '~/helpers'
import { CARD_SIZES } from './constants'

export default {
  components: {
    VideoOverlay
  },
  props: {
    size: {
      type: String,
      required: true,
      validator: value => Object.keys(CARD_SIZES).includes(value)
    },
    type: {
      type: String,
      required: false
    },
    category: {
      type: String,
      required: false
    },
    iconUrl: {
      type: String,
      required: false
    },
    fromDate: {
      type: [Object, String],
      required: false
    },
    toDate: {
      type: [Object, String],
      required: false
    },
    opensAt: {
      type: [Object, String],
      required: false
    },
    timezone: {
      type: String,
      required: false
    },
    description: {
      type: String,
      required: false
    },
    house: {
      type: [String, Array],
      required: false
    },
    houseColor: {
      type: String,
      required: false
    },
    images: {
      type: Object,
      required: false
    },
    isVideo: {
      type: Boolean,
      required: false,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: 'EXPLORE_EVENTS_EVENT_MORE_CTA'
    },
    name: {
      type: String,
      required: true
    },
    status: {
      type: String,
      required: false
    },
    guests: {
      type: Number,
      required: false
    },
    ticketless: {
      type: Boolean,
      required: false
    },
    isActive: {
      type: Boolean,
      required: false
    },
    digital: {
      type: Object
    },
    to: {
      type: [String, Object],
      required: false,
      validator: value => {
        if (typeof value === 'string') return value !== ''
        if (!value.name) return false

        return hasAllowedKeys(value, ['name', 'params'])
      }
    }
  },
  computed: {
    hasLabel () {
      return this.cardSize.showLabel
    },
    componentType () {
      return this.hasLabel || !this.to ? 'div' : 'nuxt-link'
    },
    toIfLink () {
      return this.hasLabel ? null : this.to
    },
    backgroundImage () {
      if (!this.images) return ''

      return this.images[this.cardSize.imageSize] || ''
    },
    isHouseArray () {
      return Array.isArray(this.house)
    },
    cardSize () {
      return CARD_SIZES[this.size]
    },
    houseLabel () {
      if (this.isHouseArray) {
        return this.house.join(', ')
      }
      return this.house
    },
    timeFormat () {
      if (this.type === 'house-note'
        || this.toDate) {
        return 'abrDate'
      }
      return 'abrDateTime'
    }
  }
}
</script>

<style lang="scss" scoped>

$card-shadow: 0 24px 30px -20px rgba($deprecated-grey-xx-dark, .1), 0 10px 60px 0 rgba($deprecated-grey-xx-dark, .15);
$card-border-radius: 4px;

.card {
  position: relative;
  display: block;
  text-decoration: none;
  will-change: auto;
  @include deprecated-f-color-normal;

  &a {
    cursor: pointer;
  }

  .image-container {
    width: 100%;
    min-height: 160px;
    position: relative;
  }

  .image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    will-change: auto;
    @include lazy-load;
  }

  .details {
    display: flex;
    flex-direction: row;
    padding: $space-050 0 $d-x-small 0;
    width: 100%;
    position: relative;

    .details-container {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .icon-wrapper {
      align-self: flex-end;
      height: $d-med-small;
      width: $d-med-small;
      margin-bottom: -$d-med-small;
    }

    .icon {
      height: 100%;
      width: 100%;
    }

    .date {
      @include font('030');
      text-transform: none;
      order: -1;
      margin: 0;
      margin-bottom: $d-tiny;
    }

    .name {
      @include font('080');
      margin: 0 $space-060 $space-050 0;
      padding-right: $space-030;
    }

    .house {
      @include font('030');
      margin: 0;

      &.array {
        width: 100%;
        padding: $space-060 0;
      }
    }

    .status {
      margin-top: $d-tiny;
    }

    .description {
      display: none;
      margin-top: $d-x-small;
      margin-bottom: $d-x-small;
      word-break: break-word;
      @include body-lrg;
    }

    .label {
      align-self: center;
      width: auto;
      display: inline-flex;
      justify-content: center;
      margin-top: $d-small;
      padding: $d-xx-small $d-small;
    }
  }

  &:after {
    @include transition-fast;
    left: 50%;
    width: 100%;
    transform: translate3d(-50%, 0, 0);
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    bottom: 0;
    right: 0;
    width: 100%;
    transform: translate3d(-50%, 0, 0);
    background: $color-white;
    box-shadow: 0 0 0 rgba($deprecated-grey-xx-dark, 0), 0 0 2px rgba($deprecated-grey-xx-dark, .5);
    border-radius: $card-border-radius;
    opacity: 0;
    outline: 1px solid rgba($deprecated-brand-tertiary, 0);
    outline-offset: 4px;
    visibility: hidden;
  }

  &:focus {
    .image {
      border-radius: $card-border-radius $card-border-radius 0 0;
    }
    &:before,
    &:after {
      opacity: 1;
      visibility: visible;
    }
    &:active {
      &:after {
        opacity: 0;
        visibility: hidden;
      }
    }
  }
}

// Card Style Variations
//-------------------------------------------------------------------

// Featured Cards
//-------------------------------------------------------------------

.card.featured-hero {
  display: flex;
  flex-direction: column;
  cursor: auto;

  .image-container {
    @include ratio(4, 3);
    overflow: hidden;
    @include breakpoint(medium) {
      @include ratio(16, 9);
    }
  }

  .details {
    background: $color-white;
    padding-top: $d-med;
    padding-bottom: $d-med;
    align-self: center;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    @include shadow(flyingPink);

    @include breakpoint(medium) {
      max-width: 720px;
      width: 80%;
      margin: -$d-huge $d-med 0;
      padding: $d-large $d-med;
      border-radius: $card-border-radius;
    }
    @include breakpoint(large) {
      margin-left: auto;
      margin-right: auto;
      padding: $d-med-large;
    }

    .date {
      @include font('040');
    }
  }

  .icon-wrapper {
    align-self: center;
    margin-bottom: $d-small;
    height: $d-med-large;
    width: $d-med-large;
    order: -1;
  }

  .date {
    display: block;
  }

  .name {
    @include font('090');
    margin: $d-x-small auto;
    padding: 0 $d-small;
    order: 0;
  }

  &:focus {
    &:after,
    &:before {
      display: none;
    }
  }
}

.card.featured-secondary {
  margin-top: 0;

  .image-container {
    @include ratio(2, 1.2);
    overflow: hidden;
    @include breakpoint(medium) {
      @include ratio(4, 3);
    }
  }

  .details {
    padding: $d-small 0 $d-med 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;

    .details-container {

      .venueName {
        @include font('040')
      }

      .name {
        padding: 0;
      }
    }
  }

  .icon-wrapper {
    align-self: center;
    margin-bottom: $d-tiny;
  }

  .date {
    order: 0;
  }

  .name {
    margin: $d-x-small auto;
    padding: 0 $d-x-small;
  }

  &:focus {
    .image {
      left: 0;
      width: 100%;
    }
    &:after,
    &:before {
      width: 100%;
    }
  }
}


// Block Lined Card
//-------------------------------------------------------------------

.card.featured-block {
  cursor: auto;

  .image-container {
    display: none;
  }

  .details {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;

    .icon-wrapper {
      margin-bottom: $d-med-small;
      width: 100%;

      .icon {
        display: block;
        @include horizontal-lines;
      }
    }

    .details-container {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .date {
      margin-bottom: $d-med-large;
      order: 0;
    }

    .name {
      @include font('090');

      padding: 0 $d-large;
      max-width: $bp-med;
      margin-left: auto;
      margin-right: auto;

      @include tablet {
        @include font('080');
      }
    }

    .description {
      display: block;
      padding: 0 $d-large;
      max-width: $bp-med;
      margin-left: auto;
      margin-right: auto;
    }

    .label {
      align-self: center;
    }

    .status {
      display: none;
    }
  }
}


// Card Sizs
//-------------------------------------------------------------------

.card.event-large {
  @include tablet {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .image-container {
    @include ratio(16, 9);
    @include tablet {
        flex-grow: 1;
    }
  }

  .details {
    @include tablet {
      flex-shrink: 1;
    }
  }

  .name {
    @include font('050');
    @include tablet {
      @include font('070')
    }
  }
}

.card.event-med {
  .image-container {
    @include ratio(2, 1.2);

    @include breakpoint(medium) {
      @include ratio(3, 2);
    }
  }
}

.card.event-small {
  .image-container {
    @include ratio(16, 9);
  }

  .details {
    .name {
      @include font('060');
    }
  }
}

.card.event-tiny {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: $d-tiny;
  padding-right: 0;
  width: calc(100% + #{$d-tiny});
  margin-left: -$d-tiny;

  .image-container {
    @include ratio(1, 1);
    height: 100%;
    width: 50%;
    min-height: 80px;
    max-width: 120px;
    overflow: hidden;
  }

  .details {
    margin-left: $d-tiny;
    padding: 0;
    flex-direction: column;
    justify-content: space-around;

    .icon-wrapper {
      display: none;
    }

    .name {
      @include font('050');
      margin: 0;
      margin-bottom: $d-tiny;
    }

    .date {
      margin-top: 0;
    }

    .status {
      margin-top: $d-tiny / 2;
    }
  }

  &:focus {
    transform: none;
    .image {
      width: 100%;
      left: 0;
      border-radius: 0;
    }
    &:after,
    &:before {
      width: 100%;
    }
  }
}


// Editorial Cards
//-------------------------------------------------------------------

$card-gutter: $d-xx-large;

.card {
  &.editorial-left,
  &.editorial-left-fail,
  &.editorial-right,
  &.editorial-right-fail,
  &.editorial-bleed {
    display: flex;
    flex-direction: column;
    cursor: auto;

    .image-container {
      @include ratio(3,2);
      overflow: hidden;
      @include breakpoint(medium) {
        @include ratio(4,3);
        max-height: 40vw;
        min-height: 420px;
        width: calc(100% - #{$card-gutter});
        &:before {
          display: none;
        }
      }
      @include breakpoint(large) {
        max-height: 100%;
        min-height: 560px;
      }
    }

    &:after,
    &:focus:after {
      display: none;
    }

    &:focus {
      &:after,
      &:before {
        display: none;
      }
    }

    .details {
      flex-direction: row-reverse;
      justify-content: center;
      padding: $d-med-small;
      text-align: left;

      @include breakpoint(medium) {
        flex-direction: column;
        padding: $d-xx-small;
        text-align: left;
      }

      @include breakpoint(large) {
        padding: $d-med-small;
      }

      .icon-wrapper {
        align-self: center;
        height: $d-med;
        width: $d-med;
        margin-bottom: $d-xx-small;
        @include breakpoint(medium) {
          align-self: flex-start;
        }
        @include breakpoint(large) {
          height: $d-large;
          width: $d-large;
          margin: $d-small 0;
        }
      }

      .date {
        @include font('040');
        order: 0;
      }

      .name {
        @include font('090');
        margin: $space-050 0;
        padding: 0;

        @include tablet {
          @include font('100');
        }
      }

      .label {
        align-self: flex-start;
      }

      .description {
        display: none;
        @include breakpoint(medium) {
          display: block;
        }
      }
    }
  }

  &.editorial-left,
  &.editorial-left-fail {
    @include breakpoint(medium) {
      flex-direction: row-reverse;
    }

    .details {
      @include breakpoint(medium) {
        padding-right: $card-gutter;
      }
      @include breakpoint(large) {
        padding-left: 0;
      }
    }
  }

  &.editorial-right,
  &.editorial-right-fail {
    @include breakpoint(medium) {
      flex-direction: row;
    }

    .details {
      @include breakpoint(medium) {
        padding-left: $card-gutter;
      }
      @include breakpoint(large) {
        padding-right: 0;
      }
    }
  }

  &.editorial-bleed {
    @include breakpoint(medium) {
      flex-direction: row-reverse;
    }

    .details {
      @include breakpoint(medium) {
        padding-right: $card-gutter;
        width: calc(100% - #{$card-gutter/2});
      }
      @include breakpoint(large) {
        padding-left: 0;
      }
    }
    .image-container {
      @include breakpoint(medium) {
        width: 100%;
      }
    }
  }

  &.editorial-large {
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: auto;

    .image-container {
      @include ratio(4,3);
      overflow: hidden;
      @include breakpoint(medium) {
        &:before {
          display: none;
        }
      }
    }

    &:after,
    &:focus:after {
      display: none;
    }

    &:focus {
      &:after,
      &:before {
        display: none;
      }
    }

    .details {
      flex-direction: row-reverse;
      align-content: center;
      text-align: center;
      padding: $d-med-small;
      @include breakpoint(medium) {
        flex-direction: column;
        padding: $d-large;
      }

      .details-container {
        flex-grow: 1;
      }

      .icon-wrapper {
        height: $d-med;
        width: $d-med;
        align-self: center;
        margin-bottom: $space-040;

        @include breakpoint(medium) {
          margin-top: 0;
        }
      }

      .date {
        order: 0;
      }

      .name {
        margin: $d-x-small auto;
      }
    }

    @include breakpoint(medium) {
      position: relative;
      min-height: 560px;
      padding-top: $d-xx-large * 2;
      padding-bottom: $d-xx-large * 2;

      .image-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      .details {
        flex-basis: fit-content;
        width: auto;
        max-width: calc(100% - #{$d-xx-large * 4});
        margin: auto;
        border-radius: 4px;
        background-color: rgba($color-white, .95);
        background: $color-white;
        @supports (backdrop-filter: blur()){
          background-color: rgba($color-white, .8);
          backdrop-filter: blur(40px) brightness(150%);
        }
      }
      .details-container {
        padding-left: $d-med;
        padding-right: $d-med;
      }
    }
  }
}

.promotional {
  .card {
    .details .description {
      display: block;
    }
    .venueName {
      color: $color-gray-4;
      padding: $space-030 0;
    }
  }
}

// House Note Card
//-------------------------------------------------------------------

.house-note .card {
  .details-container {
    .description {
      @include body-med;
      @include deprecated-f-color-dark;
    }
  }

  &.event-med,
  &.event-small,
  &.event-large {
    .details-container {
      .icon-wrapper {
        display: none;
      }
      .name {
        @include font('070');

        order: -1;
        margin-bottom: $d-tiny;
        margin-right: 0;
        padding-right: 0;
      }

      .date {
        display: none;
      }

      .description {
        display: block;
        margin-top: 0;
      }
    }
  }
}


// Empty Card
//-------------------------------------------------------------------
.empty.card {
  .details {
    .name {
      @include font('070');
    }
    .description {
      display: block;
    }
  }

  .image-container {
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    .image {
      background-size: contain;
    }
  }
}
</style>
